import React, { useEffect, useState } from "react";
import Image from "next/image";
import Modal from "react-modal";
import { RichText } from "prismic-reactjs";
import ReactPlayer from "react-player";
import { handleTextAlign } from "../../../../helpers/textAlignPost";
import {
  formatListItems,
  formatOrderedListItems,
  formatSubTitle,
  formatText,
  formatTitle,
} from "../../formatText";
import CodeBlockWithCopyButton from "../../CodeBlockWithCopyButton";

const ImageAbove = ({ block }) => {
  const primary = block?.primary;
  const items = block?.items;

  const [isOpen, setIsOpen] = useState(false);
  const [modalImage, setModalImage] = useState(null);

  const openModal = (image) => {
    if (window.innerWidth >= 581) {
      setModalImage(image);
      setIsOpen(true);
    }
  };

  const closeModal = () => {
    setIsOpen(false);
    setModalImage(null);
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  const getDynamicStyles = () => {
    return `
      .post-above-content {
        width: 100%;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: flex-start;
        overflow: hidden;
        gap: 20px;
        margin: 20px 0;
      }
      .post-above-text {
        width: 100%;
        display: flex;
        flex-direction: column;
        text-align: center;
      }
      .post-above-text > span > h1 {
        margin: 0;
        font-family: 'Roboto-Normal';
        font-size: 25px;
        font-weight: bold;
      }
      .post-above-text > div > p {
        margin: 10px 0;
        font-family: 'Roboto-Normal';
        font-size: 16px;
      }
      .post-above-text a {
        color: #0070f3;
        text-decoration: underline;
        font-weight: bold;
      }
      .post-above-image {
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        position: relative;
      }
      .image-above-wrapper {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100%;
        max-width: 1920px;
        max-height: 1920px;
      }
      .post-above-image-mobile {
        display: none;
      }
      .text-block-above > span > h2 {
        font-size: 20px;
        margin-top: 0;
        text-align: left;
      }
      .text-block-above > p {
        margin: 10px 0;
        font-family: 'Roboto-Normal';
        font-size: 16px;
        text-align: justify;
      }
      .list-container ul,
      .o-list-container ol {
        padding-left: 20px;
        text-align: left;  
      }
      .list-container li,
      .o-list-container li {
        margin: 5px 0; 
        list-style-position: outside;
      }
      .list-container li {
        list-style-type: disc;
      }
      .o-list-container li {
        list-style-type: number;
      }
      .post-above-description-mobile {
        display: none;
      }
      @media (max-width: 767px) {
        .post-above-content {
          flex-direction: column;
          gap: 20px;
          margin: 0;
          padding: 10px 0;
        }
        .post-above-description {
          display: none;
        }
        .post-above-description-mobile {
          order: 3;
          max-width: 100%;
          gap: 20px;
          padding: 0;
          display: flex;
          flex-direction: column;
        }
        .post-above-text {
          max-width: 100%;
          gap: 20px;
          padding: 0;
        }
        .post-above-text > span > h1 {
          font-size: 16px;
        }
        .post-above-text > div > p {
          font-size: 13px;
          margin: 0;
          text-align: justify;
        }
        .text-block-above > span > h2 {
          font-size: 15px;
          margin-top: 0;
        }
        .list-container > ul,
        .o-list-container > ol {
          font-family: 'Roboto-Normal';
          font-size: 13px;
        }
        .post-above-image {
          order: 2;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
        }
      }
      @media (max-width: 430px) {
        .post-above-text > span > h1,
        .post-above-text > div > p {
          text-align: justify;
        }
      }
    `;
  };

  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.type = "text/css";
    styleSheet.innerText = getDynamicStyles();
    document.head.appendChild(styleSheet);

    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  return (
    <>
      {primary && (
        <div className="post-above-content">
          {primary.media.kind === "video" ? (
            <div className="post-above-image">
              <div className="image-above-wrapper">
                <ReactPlayer
                  url={primary.media.url}
                  width={"100%"}
                  height={"100%"}
                  controls={true}
                  playing={false}
                  muted={true}
                  loop={false}
                />
              </div>
            </div>
          ) : (
            primary.media.url !== undefined && (
              <div
                className="post-above-image"
                onClick={() => openModal(primary.media.url)}
              >
                <div
                  className="image-above-wrapper"
                  style={{
                    height: `${primary.media.height}`,
                  }}
                >
                  <Image
                    src={primary.image.url}
                    layout="intrinsic"
                    width={
                      primary.media_width !== null
                        ? primary.media_width
                        : primary.media.width
                    }
                    height={
                      primary.media_height !== null
                        ? primary.media_height
                        : primary.media.height
                    }
                    alt={
                      primary?.media_alt_text !== null
                        ? primary?.media_alt_text
                        : "Post Image"
                    }
                    loading="lazy"
                  />
                </div>
              </div>
            )
          )}
          {items.length > 0 && (
            <div className="post-above-description-mobile">
              {items.map((item, index) => {
                const listItems = item.description.filter(
                  (des) => des.type === "list-item"
                );
                const orderedListItems = item.description.filter(
                  (des) => des.type === "o-list-item"
                );
                const paragraph = item.description.filter(
                  (des) =>
                    des.type !== "list-item" && des.type !== "o-list-item"
                );
                const id = item?.sub_title[0]?.text
                  ?.split(" ")
                  .join("-")
                  .toLowerCase();
                return (
                  <div key={index} className="text-block-above">
                    {item?.sub_title.length > 0 && (
                      <span
                        dangerouslySetInnerHTML={{
                          __html: formatSubTitle(item),
                        }}
                        id={id}
                      />
                    )}
                    {paragraph?.length > 0 && (
                      <p
                        key={index}
                        style={
                          primary.content_title.length > 0
                            ? {}
                            : { marginTop: "0px" }
                        }
                        dangerouslySetInnerHTML={{
                          __html: formatText(
                            RichText.asText(paragraph),
                            item.description[0]?.spans,
                            item.underline_description_text,
                            item.description_color_for_parts,
                            item.description_color_parts
                          ),
                        }}
                      />
                    )}
                    {listItems?.length > 0 && (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: formatListItems(
                            listItems,
                            item.underline_description_text,
                            item.description_color_for_parts,
                            item.description_color_parts
                          ),
                        }}
                        className="list-container"
                      />
                    )}
                    {orderedListItems?.length > 0 && (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: formatOrderedListItems(
                            orderedListItems,
                            item.underline_description_text,
                            item.description_color_for_parts,
                            item.description_color_parts
                          ),
                        }}
                        className="o-list-container"
                      />
                    )}
                    {item.code_block.length > 0 && (
                      <CodeBlockWithCopyButton
                        code={RichText.asText(item.code_block)}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <div className="post-above-text">
            {primary?.content_title.length > 0 && (
              <span
                dangerouslySetInnerHTML={{ __html: formatTitle(primary) }}
                style={{
                  textAlign: handleTextAlign(primary.title_align),
                }}
              />
            )}
            {items.length > 0 && (
              <div className="post-above-description">
                {items.map((item, index) => {
                  const listItems = item.description.filter(
                    (des) => des.type === "list-item"
                  );
                  const orderedListItems = item.description.filter(
                    (des) => des.type === "o-list-item"
                  );
                  const paragraph = item.description.filter(
                    (des) =>
                      des.type !== "list-item" && des.type !== "o-list-item"
                  );
                  const id = item?.sub_title[0]?.text
                    ?.split(" ")
                    .join("-")
                    .toLowerCase();
                  return (
                    <div key={index} className="text-block-above">
                      {item?.sub_title.length > 0 && (
                        <span
                          dangerouslySetInnerHTML={{
                            __html: formatSubTitle(item),
                          }}
                          id={id}
                        />
                      )}
                      {paragraph?.length > 0 && (
                        <p
                          key={index}
                          style={
                            primary.content_title.length > 0
                              ? {}
                              : { marginTop: "0px" }
                          }
                          dangerouslySetInnerHTML={{
                            __html: formatText(
                              RichText.asText(paragraph),
                              item.description[0]?.spans,
                              item.underline_description_text,
                              item.description_color_for_parts,
                              item.description_color_parts
                            ),
                          }}
                        />
                      )}
                      {listItems?.length > 0 && (
                        <div
                          dangerouslySetInnerHTML={{
                            __html: formatListItems(
                              listItems,
                              item.underline_description_text,
                              item.description_color_for_parts,
                              item.description_color_parts
                            ),
                          }}
                          className="list-container"
                        />
                      )}
                      {orderedListItems?.length > 0 && (
                        <div
                          dangerouslySetInnerHTML={{
                            __html: formatOrderedListItems(
                              orderedListItems,
                              item.underline_description_text,
                              item.description_color_for_parts,
                              item.description_color_parts
                            ),
                          }}
                          className="o-list-container"
                        />
                      )}
                      {item.code_block.length > 0 && (
                        <CodeBlockWithCopyButton
                          code={RichText.asText(item.code_block)}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
      <Modal
        isOpen={isOpen}
        onRequestClose={closeModal}
        ariaHideApp={false}
        style={{
          overlay: {
            backgroundColor: "rgba(0, 0, 0, 0.75)",
            zIndex: 1000,
          },
          content: {
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "80vw",
            height: "80vh",
            border: "none",
            padding: "0",
            overflow: "hidden",
            zIndex: 1001,
            background: "none",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          },
        }}
      >
        {modalImage && (
          <div className="modal-content">
            <img
              src={modalImage.url}
              alt="Expanded Image"
              className="image-container"
            />
            <button className="close-button" onClick={closeModal}>
              <Image
                draggable={false}
                src="https://cdn.shopify.com/s/files/1/0522/3320/7988/files/icon-close.png?v=1714038756"
                alt="icon-search"
                width={34}
                height={34}
                loading="lazy"
              />
            </button>
          </div>
        )}
      </Modal>

      <style jsx>{`
        .modal-content {
          width: 100%;
          height: 100%;
          display: flex;
          justify-content: center;
          align-items: flex-start;
        }
        .image-container {
          max-width: 90%;
          max-height: 90%;
          width: auto;
          height: auto;
          object-fit: contain;
        }
        .close-button {
          border: none;
          background-color: transparent;
          padding: 5px 10px;
          cursor: pointer;
          z-index: 1002;
        }
      `}</style>
    </>
  );
};

export default ImageAbove;
